import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "./Sidebar";
import { cn } from "../../lib/utils";
import {
  PieChart, LayoutDashboard, Users, Settings, Menu, LogOut,
} from "lucide-react";

// Bottom tab bar — replaces the desktop sidebar on mobile. Mimics native iOS /
// Android tab bars (e.g. CRED, Jupiter, Kuvera). Each role gets a tailored
// tab set; clients see only what's relevant to them.
const MobileBottomNav = () => {
  const { user, logout } = useAuth();
  const { isMobile, setMobileOpen } = useSidebar();
  const navigate = useNavigate();

  if (!isMobile) return null;
  if (!user) return null;

  const handleLogout = () => {
    if (!window.confirm("Sign out of Kinntegra?")) return;
    logout();
    navigate("/about", { replace: true });
  };

  // Tab config per role. Clients only have Portfolio in the app, so we add
  // a "Profile" tab for account/security and a Logout tab so they can sign
  // out without needing to dig into a menu.
  let tabs;
  if (user.role === "client") {
    tabs = [
      { label: "Portfolio", icon: PieChart, path: "/portfolio" },
      { label: "Profile", icon: Settings, path: "/settings" },
      { label: "Logout", icon: LogOut, path: null, action: handleLogout },
    ];
  } else if (user.role === "sub_broker") {
    tabs = [
      { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
      { label: "Clients", icon: Users, path: "/client-wise" },
      { label: "Portfolio", icon: PieChart, path: "/portfolio" },
      { label: "More", icon: Menu, path: null, action: () => setMobileOpen(true) },
    ];
  } else {
    tabs = [
      { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
      { label: "Clients", icon: Users, path: "/client-wise" },
      { label: "Portfolio", icon: PieChart, path: "/portfolio" },
      { label: "More", icon: Menu, path: null, action: () => setMobileOpen(true) },
    ];
  }

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 bg-[rgb(var(--brand-1))] border-t border-white/5 shadow-[0_-2px_12px_rgba(0,0,0,0.4)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-bottom-nav"
    >
      <ul className={cn("flex items-center justify-around", tabs.length === 2 ? "px-12" : "")}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          if (!tab.path && tab.action) {
            return (
              <li key={tab.label} className="flex-1">
                <button
                  onClick={tab.action}
                  className="flex flex-col items-center justify-center gap-0.5 w-full py-2 text-white/55 active:text-[rgb(var(--brand-2))] active:scale-95 transition-transform"
                  data-testid={`bottom-nav-${tab.label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium">{tab.label}</span>
                </button>
              </li>
            );
          }
          return (
            <li key={tab.label} className="flex-1">
              <NavLink
                to={tab.path}
                data-testid={`bottom-nav-${tab.label.toLowerCase()}`}
                className={({ isActive }) =>
                  cn(
                    "flex flex-col items-center justify-center gap-0.5 w-full py-2 active:scale-95 transition-transform",
                    isActive ? "text-[rgb(var(--brand-2))]" : "text-white/55"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={cn(
                        "relative flex items-center justify-center w-10 h-7 rounded-full transition-colors",
                        isActive ? "bg-[rgb(var(--brand-2)/0.15)]" : ""
                      )}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-medium">{tab.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default MobileBottomNav;
